import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import Button from '../../components/common/Button';
import { api } from '../../services/api';
import { Close, CloudUpload, CheckCircle } from '@mui/icons-material';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.lg};
  padding: ${props => getTheme(props.theme?.isDark).spacing.xl};
  width: 90%;
  max-width: 600px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  cursor: pointer;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  
  &:hover {
    background-color: ${props => getTheme(props.theme?.isDark).colors.hover};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const FileInput = styled.input`
  width: 100%;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
  }
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 14px;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background-color: #fee2e2;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const SuccessText = styled.div`
  color: #065f46;
  font-size: 14px;
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background-color: #d1fae5;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const FileInfo = styled.div`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surfaceElevated};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const UploadAppModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const { isDark } = useTheme();
  const theme = { isDark };

  const [appName, setAppName] = useState('');
  const [customId, setCustomId] = useState('');
  const [visibility, setVisibility] = useState('individual');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.apk', '.aab', '.ipa'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!validExtensions.includes(fileExtension)) {
        setError('Yalnız APK, AAB və ya IPA faylları yüklənə bilər');
        setSelectedFile(null);
        return;
      }

      // Validate file size (max 500MB)
      const maxSize = 500 * 1024 * 1024; // 500MB
      if (file.size > maxSize) {
        setError('Fayl ölçüsü 500MB-dan böyük ola bilməz');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError('');
      
      // Auto-fill app name if empty
      if (!appName) {
        const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
        setAppName(nameWithoutExt);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(null);

    if (!selectedFile) {
      setError('Fayl seçilməlidir');
      return;
    }

    if (!appName) {
      setError('App adı tələb olunur');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('appFile', selectedFile);
      formData.append('appName', appName);
      if (customId) {
        formData.append('customId', customId);
      }
      formData.append('visibility', visibility);

      const result = await api.uploadAppToLambdaTest(formData);

      setSuccess({
        appId: result.app_id,
        appUrl: result.app_url || result.url,
        name: result.name,
        type: result.type,
      });

      // Reset form after 2 seconds
      setTimeout(() => {
        if (onUploadSuccess) {
          onUploadSuccess(result);
        }
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'App yüklənmədi');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setAppName('');
    setCustomId('');
    setVisibility('individual');
    setSelectedFile(null);
    setError('');
    setSuccess(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <ModalTitle theme={theme}>App Yüklə (LambdaTest)</ModalTitle>
          <CloseButton theme={theme} onClick={handleClose}>
            <Close />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          {error && <ErrorText theme={theme}>{error}</ErrorText>}
          {success && (
            <SuccessText theme={theme}>
              <CheckCircle style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              App uğurla yükləndi! App ID: {success.appId}
            </SuccessText>
          )}

          <FormGroup theme={theme}>
            <Label theme={theme}>App Faylı (APK, AAB, IPA)</Label>
            <FileInput
              theme={theme}
              type="file"
              accept=".apk,.aab,.ipa"
              onChange={handleFileChange}
              required
            />
            {selectedFile && (
              <FileInfo theme={theme}>
                <div><strong>Fayl:</strong> {selectedFile.name}</div>
                <div><strong>Ölçü:</strong> {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</div>
              </FileInfo>
            )}
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>App Adı *</Label>
            <Input
              theme={theme}
              type="text"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              placeholder="Fit Android Debug"
              required
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Custom ID (İstəyə bağlı)</Label>
            <Input
              theme={theme}
              type="text"
              value={customId}
              onChange={(e) => setCustomId(e.target.value)}
              placeholder="fit-android-debug"
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>Görünürlük</Label>
            <Select
              theme={theme}
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
            >
              <option value="individual">Individual (Şəxsi)</option>
              <option value="team">Team (Komanda)</option>
            </Select>
          </FormGroup>

          <div style={{ display: 'flex', gap: getTheme(isDark).spacing.sm, marginTop: getTheme(isDark).spacing.lg }}>
            <Button
              type="submit"
              variant="primary"
              theme={theme}
              style={{ flex: 1 }}
              disabled={uploading || success}
            >
              <CloudUpload /> {uploading ? 'Yüklənir...' : 'App Yüklə'}
            </Button>
            <Button
              type="button"
              variant="outline"
              theme={theme}
              onClick={handleClose}
              disabled={uploading}
            >
              Ləğv et
            </Button>
          </div>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default UploadAppModal;


